import { Box } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { DEFAULT_WAIT_SECONDS } from 'components/main/listeners/queue';
import { MachineContext } from 'contexts/machine.context';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { IBaseDialog } from 'interfaces/i-dialogs';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IQueueMsg } from 'lib_ts/interfaces/machine-msg/i-queue-utilities';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { WebSocketService } from 'services/web-socket.service';
import useMachineStatusStore from 'stores/use-machine-status-store';

const COMPONENT_NAME = 'MachineControlDialog';

export const MachineControlDialog: FC<IBaseDialog> = (props) => {
  const activeUser = useMachineStatusStore((s) => s.activeUser);

  const { machine } = useContext(MachineContext);

  const countdownInterval = useRef<NodeJS.Timer>();

  /** before taking control by force */
  const [seconds, setSeconds] = useState(0);
  const latestSeconds = useLatest(seconds);

  // handle if the active user responds to the request
  useEffect(() => {
    const callback = (event: CustomEvent) => {
      const data: IQueueMsg = event.detail;

      switch (data.action) {
        case 'active-accept': {
          /** active user accepted your request */
          NotifyHelper.success({
            message_md: 'Access request accepted, transferring control...',
          });
          break;
        }

        case 'active-reject': {
          /** active user accepted your request */
          NotifyHelper.warning({
            message_md:
              'The active user has rejected your request, please try again later.',
          });
          break;
        }

        default: {
          break;
        }
      }

      props.onClose();
    };

    WebSocketHelper.on(WsMsgType.Misc_ControlResponse, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.Misc_ControlResponse, callback);
    };
  }, []);

  // cleanup the interval in case the dialog unmounts before anything happens
  useEffect(() => {
    return () => clearInterval(countdownInterval.current);
  }, []);

  const handleConfirmation = (confirmed: boolean) => {
    if (!confirmed) {
      // unmounting will cancel the request and the active user's dialog will just timeout
      props.onClose();
      return;
    }

    // send the request and start an interval to start the countdown
    WebSocketService.send(WsMsgType.Misc_ControlRequest, {}, 'SidebarFooter');

    setSeconds(DEFAULT_WAIT_SECONDS);

    clearInterval(countdownInterval.current);

    countdownInterval.current = setInterval(() => {
      const nextSeconds = latestSeconds.current - 1;
      setSeconds(nextSeconds);

      if (nextSeconds > 0) {
        return;
      }

      clearInterval(countdownInterval.current);

      // take control of the machine by force
      WebSocketService.send(WsMsgType.Misc_ControlForce, {}, 'SidebarFooter');

      props.onClose();
    }, 1_000);
  };

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        title="common.machine-control-request"
        width={RADIX.DIALOG.WIDTH.SM}
        content={
          <Box>
            {seconds > 0 ? (
              <ReactMarkdown
                children={t('common.machine-control-requester-waiting-msg', {
                  username: activeUser,
                  seconds: seconds,
                })}
              />
            ) : (
              <ReactMarkdown
                children={t('common.machine-control-requester-msg', {
                  username: activeUser,
                  machineID: machine.machineID,
                })}
              />
            )}
          </Box>
        }
        buttons={[
          {
            label: 'common.request-control',
            disabled: seconds > 0,
            onClick: () => handleConfirmation(true),
          },
          {
            label: 'common.cancel',
            onClick: () => handleConfirmation(false),
          },
        ]}
        onClose={() => handleConfirmation(false)}
      />
    </ErrorBoundary>
  );
};
